/** Visible trust warning for profit and cash cards that depend on historical overhead. */

export interface OverheadHistorySummary {
  recordedMonths: number;
  sparse: boolean;
  trust: "none" | "manual" | "accounting_pending" | "accounting_approved";
  fallbackMonths: number;
}

export function OverheadHistoryNotice({ history }: {
  history?: OverheadHistorySummary | null;
}): React.ReactElement | null {
  if (!history) return null;
  if (history.trust === "accounting_pending") {
    return (
      <div role="status" style={notice}>
        <span aria-hidden="true">⚠️</span>
        <span>
          Your accounting expenses have not been confirmed yet. Profit and cash cards that need overhead stay locked until you review them.
        </span>
      </div>
    );
  }
  if (history.trust === "accounting_approved" && history.fallbackMonths > 0) {
    const months = history.fallbackMonths;
    return (
      <div role="status" style={notice}>
        <span aria-hidden="true">⚠️</span>
        <span>
          {months} month{months === 1 ? " does" : "s do"} not have a complete accounting report. {months === 1 ? "That month uses" : "Those months use"} your approved planning amount instead of actual expenses.
        </span>
      </div>
    );
  }
  if (!history.sparse) return null;
  const months = history.recordedMonths;
  return (
    <div role="status" style={notice}>
      <span aria-hidden="true">⚠️</span>
      <span>
        We only have {months} month{months === 1 ? "" : "s"} of overhead history. Earlier months use your earliest recorded amount, so past profit and cash timing are still estimates. Use dates covered by that history for the cleanest read, or add older overhead records to make earlier months accurate.
      </span>
    </div>
  );
}

const notice: React.CSSProperties = {
  display: "flex",
  gap: 7,
  marginTop: 9,
  padding: "9px 11px",
  borderRadius: "var(--r-sm)",
  border: "1px solid color-mix(in srgb, var(--warn, #b5820a) 45%, var(--border))",
  background: "color-mix(in srgb, var(--warn, #b5820a) 9%, var(--surface))",
  color: "var(--body)",
  fontSize: 11.5,
  lineHeight: 1.5,
};
